/* eslint-disable @typescript-eslint/no-explicit-any */
import { Notification } from 'ksnm-common-ui/lib/Notification';
import type { ReactElement } from 'react';

import { makeExtendError } from './makeExtendError';
import type { Api, ErrorResponse } from './api.generated';

function showUnexpectedErrorNotification(): void {
  Notification({
    key: 'unexpectedErrorNotification',
    message: 'Непредвиденная ошибка',
  });
}

function showErrorNotification(message: string | ReactElement): void {
  Notification({
    key: 'unexpectedErrorNotification',
    message,
  });
}

interface ErrorMessagesInterface {
  message?: string;
  path?: string;
}

type ApiResponse = {
  data: unknown;
  error: ErrorResponse | null;
  ok?: boolean;
  errors?: ErrorMessagesInterface[];
};

export function fetchWrapper<P extends unknown[], R extends ApiResponse>(
  fn: (...arg: P) => Promise<R>,
): (...arg: P) => Promise<R> {
  return async (...args: P): Promise<R> => {
    try {
      const response = await fn(...args);
      // Hack. Really we don't need to check result.ok, but if response is not a json we get parsing json error,
      // because swagger-typescript-api ?always try to parse json
      if (response.error && !response.ok) {
        if (response.errors && Array.isArray(response.errors) && response.errors.length > 0) {
          const message = response.errors.map((item) => `${item.path} ${item.message}`).join(', ');
          showErrorNotification(message);
        } else {
          showUnexpectedErrorNotification();
        }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return response;
    } catch (error: any) {
      // TODO: надо переписать хендлер на более вменяемые ошибки, но это еще с бэком связано
      if (
        error &&
        error.error &&
        error.error.errors &&
        Array.isArray(error.error.errors) &&
        error.error.errors.length > 0
      ) {
        const message = error.error.errors
          .map((item: ErrorMessagesInterface) => `${item.path ?? ''} ${item.message}`)
          .join(', ');
        showErrorNotification(message);
      } else {
        if (error.body && error.body.pipeThrough) {
          const message = await new Promise<string | React.ReactElement | null>(async (resolve) => {
            const reader = await new Promise<any>((done) => {
              try {
                done(error.body.pipeThrough(new TextDecoderStream()).getReader());
              } catch {
                done(null);
              }
            });
            if (reader) {
              const chunks: string[] = [];
              reader.read().then(function process(obj: any) {
                const { done, value } = obj;
                if (done) {
                  try {
                    const errorsObj = JSON.parse(chunks.join(''));
                    if (Array.isArray(errorsObj.errors) && errorsObj.errors.length > 0) {
                      resolve(errorsObj.errors[0].message);
                    } else if (errorsObj.trace) {
                      resolve(makeExtendError(errorsObj));
                    } else {
                      resolve(null);
                    }
                  } catch {
                    resolve(null);
                  }
                } else {
                  chunks.push(value);
                  return reader.read().then(process);
                }
              });
            } else if (error.error && error.error.error) {
              resolve(makeExtendError(error.error));
            } else {
              showUnexpectedErrorNotification();
              resolve(null);
            }
          });
          if (message) {
            showErrorNotification(message);
          } else {
            showUnexpectedErrorNotification();
          }
        } else {
          if (error.name !== 'AbortError') {
            showUnexpectedErrorNotification();
          }
        }
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return { data: null, error };
    }
  };
}

export function withErrorHandler<T = Api<unknown>>(api: T): T {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return (Object.keys(api) as [keyof T]).reduce((acc, moduleName) => {
    const apiModule = api[moduleName];

    if (!apiModule) {
      return acc;
    }

    return {
      ...acc,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [moduleName]: Object.entries(api[moduleName]).reduce(
        (moduleRequests, [fnName, fn]) => ({
          ...moduleRequests,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          [fnName]: fetchWrapper(fn),
        }),
        {} as T,
      ),
    };
  }, {} as T);
}
